import React, { useState, useEffect } from 'react';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [cookiesDetected, setCookiesDetected] = useState([]);

  useEffect(() => {
    // Check if user has already given consent
    const consentGiven = localStorage.getItem('cookieConsent');
    
    if (!consentGiven) {
      // Detect existing cookies
      detectCookies();
      setShowBanner(true);
    }
  }, []);

  const detectCookies = () => {
    const cookies = document.cookie.split(';').map(cookie => {
      const [name, value] = cookie.trim().split('=');
      return { name, value };
    }).filter(cookie => cookie.name);

    setCookiesDetected(cookies);
    
    // Log cookie detection for debugging
    console.log('🍪 Cookies detected:', cookies);
  };

  const acceptCookies = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      accepted: true,
      timestamp: new Date().toISOString(),
      cookiesAtConsent: cookiesDetected
    }));
    setShowBanner(false);
  };

  const rejectCookies = () => {
    // Clear all cookies (except essential ones)
    document.cookie.split(";").forEach(cookie => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      
      // Don't clear essential cookies (add your essential cookie names here)
      const essentialCookies = ['session', 'auth', 'csrf'];
      if (!essentialCookies.includes(name)) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      }
    });

    localStorage.setItem('cookieConsent', JSON.stringify({
      accepted: false,
      timestamp: new Date().toISOString(),
      cookiesAtConsent: cookiesDetected
    }));
    setShowBanner(false);
  };

  const openCookieSettings = () => {
    // Show simplified cookie information
    alert(`🍪 Cookie Information:

Essential Cookies: Required for secure login and basic functionality.

Analytics/Marketing: We don't use any tracking, advertising, or analytics cookies.

Your Choice: You can accept essential cookies only or decline optional ones.

More Info: You can change your preferences anytime by clearing your browser data.`);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-blue-500 shadow-lg z-50">
      <div className="max-w-7xl mx-auto p-4">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">🍪</span>
              <h3 className="font-semibold text-gray-900">Cookie Notice</h3>
              {cookiesDetected.length > 0 && (
                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                  {cookiesDetected.length} cookie(s) detected
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-2">
              This site uses essential cookies for secure login. We don't use tracking cookies.
              <button 
                onClick={openCookieSettings}
                className="text-blue-600 hover:text-blue-800 underline ml-1"
              >
                Learn more
              </button>
            </p>
            <div className="text-xs text-gray-500">
              🔒 Your therapy data is end-to-end encrypted and GDPR compliant
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={rejectCookies}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Reject Optional
            </button>
            <button
              onClick={acceptCookies}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Accept All
            </button>
          </div>
        </div>
        
        {/* Cookie details toggle */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <details className="text-xs text-gray-600">
            <summary className="cursor-pointer hover:text-blue-600">
              🔍 Technical Details ({cookiesDetected.length} cookies detected)
            </summary>
            <div className="mt-2 p-2 bg-gray-50 rounded text-xs font-mono">
              {cookiesDetected.length === 0 ? (
                <div className="text-green-600">✅ No cookies currently set</div>
              ) : (
                cookiesDetected.map((cookie, index) => (
                  <div key={index} className="mb-1">
                    <span className="font-semibold">{cookie.name}:</span> 
                    <span className="text-gray-500"> {cookie.value.substring(0, 30)}...</span>
                  </div>
                ))
              )}
            </div>
          </details>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
